import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';
import { shortHash } from '../engine/hash';

const WEBAUDIO_SOURCE = 'https://www.w3.org/TR/webaudio/';
const VADREVU_AUDIO =
  'https://arxiv.org/pdf/2107.14201.pdf';

/**
 * Web Audio OfflineAudioContext fingerprint + speechSynthesis voice list.
 *
 * This is DISTINCT from our existing `audio` probe (AudioWorklet real-time
 * jitter, a VM/host signal). This one is the classic offline-rendering
 * fingerprint: float arithmetic across the audio graph produces slightly
 * different sample values per implementation, and the composite is
 * high-entropy and very hard to spoof because it depends on the whole DSP
 * pipeline. Offline rendering means no sound is ever produced.
 *
 * The voice list is included because speechSynthesis.getVoices() is one of the
 * highest-entropy platform signals available (it reveals installed language
 * packs) and is often left untouched by anti-detect browsers that bother to
 * patch canvas.
 *
 * Credit: surface selection from CreepJS (MIT) src/audio, src/speech; the
 * diversity/feasibility analysis is Vadrevu et al.
 */

export const audioFpProbe: Probe = {
  id: 'audioFp',
  label: 'Audio fingerprint & voices',
  layer: 'hardware',
  tier: 'client',
  category: 'hardware',
  description: 'OfflineAudioContext render hash and speechSynthesis voice inventory',
  sources: [WEBAUDIO_SOURCE, VADREVU_AUDIO],
  budgetMs: 5000,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];

    // --- 1. offline audio render fingerprint -------------------------------
    // A fixed oscillator + dynamics compressor chain. The output samples differ
    // measurably between implementations even for identical parameters.
    const OfflineCtx =
      (globalThis as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext;
    let audioHash: string | null = null;
    let sampleSum: number | null = null;
    let sampleCount = 0;
    let audioError: string | null = null;

    if (OfflineCtx) {
      try {
        const ctx = new OfflineCtx(1, 44100, 44100);
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = 10000;

        const comp = ctx.createDynamicsCompressor();
        // Firefox < 73 threw on values outside these ranges.
        comp.threshold.value = -50;
        comp.knee.value = 40;
        comp.ratio.value = 12;
        comp.attack.value = 0;
        comp.release.value = 0.25;

        osc.connect(comp);
        comp.connect(ctx.destination);
        osc.start(0);

        const buffer = await ctx.startRendering();
        const channel = buffer.getChannelData(0);
        sampleCount = channel.length;
        let sum = 0;
        // Sum in a fixed order; rounding differences are the signal.
        for (let i = 0; i < channel.length; i++) sum += channel[i];
        sampleSum = sig(sum, 6);
        audioHash = await shortHash(Array.from(channel.slice(0, 4096)).join(','));
      } catch (e) {
        audioError = String((e as Error)?.message ?? e);
        // The Firefox compressor-range throw is itself a fingerprint.
        if (/outside|range|not a number/i.test(audioError)) {
          findings.push({
            id: 'audiofp.compressor-range-error',
            severity: 'low',
            category: 'hardware',
            layer: 'hardware',
            confidence: 'low',
            title: 'DynamicsCompressor parameter range error',
            detail:
              'Setting compressor parameters threw a range error. This matched a known Firefox <73 behaviour where the compressor rejected out-of-range values, and can also indicate a non-conformant or proxied AudioWorklet.',
            sources: [WEBAUDIO_SOURCE],
            evidence: [audioError],
            scoreContribution: 0,
          });
        }
      }
    }

    // --- 2. speechSynthesis voices ----------------------------------------
    // getVoices() is async-populated in Chromium; give it a moment.
    const voices = await readVoices();

    return {
      signals: {
        available: ok(OfflineCtx != null, 'client'),
        offlineAudioSupported: ok(OfflineCtx != null, 'client'),
        audioRenderHash: ok(audioHash, 'derived'),
        audioSampleSum: ok(sampleSum, 'derived'),
        audioSampleCount: ok(sampleCount, 'client'),
        audioError: ok(audioError, 'client'),
        // Speech voices (high entropy, often unpatched)
        voiceCount: ok(voices.length, 'client'),
        voices: ok(
          voices.map((v) => ({ name: v.name, lang: v.lang, local: v.localService, default: v.default })),
          'client',
        ),
        voiceLangs: ok([...new Set(voices.map((v) => v.lang))].sort(), 'derived'),
        speechSupported: ok(typeof speechSynthesis !== 'undefined', 'client'),
      },
      findings,
      parts: {
        media: {
          // The offline render hash IS a stable platform property (float
          // arithmetic over a fixed DSP graph), so it is included.
          audioFpHash: audioHash,
          // DELIBERATELY EXCLUDED: voiceLangs. speechSynthesis.getVoices() is
          // async-populated in Chromium — it can return [] on an early scan and
          // the full list a moment later for the SAME browser, which made the
          // media part drift between two scans. The voice list is one of the
          // highest-entropy signals available, so this is a real loss, but an
          // unstable part is worse than no part. The voices are still shown as
          // signals so a reader can inspect them; only the hash omits them.
        },
      },
    };
  },
};

/** Read speechSynthesis voices, tolerating the async population in Chromium. */
function readVoices(timeoutMs = 1200): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    if (typeof speechSynthesis === 'undefined') return resolve([]);
    const now = speechSynthesis.getVoices();
    if (now.length) return resolve(now);
    const onChange = () => {
      const v = speechSynthesis.getVoices();
      if (v.length) {
        speechSynthesis.removeEventListener('voiceschanged', onChange);
        resolve(v);
      }
    };
    speechSynthesis.addEventListener('voiceschanged', onChange);
    setTimeout(() => {
      speechSynthesis.removeEventListener('voiceschanged', onChange);
      resolve(speechSynthesis.getVoices());
    }, timeoutMs);
  });
}

registerProbe(audioFpProbe);
